// Advanced Rich Media components (7.6E) as typed CreativeOperation[] — the
// same operation language as every other Studio edit and as future AI
// authoring (ADR-0018 §2.1-6). Nothing here validates or repairs: the
// Creative Engine accepts or rejects every batch (applyAndValidate).
//
//   video / audio   primitives with a PlaybackIntent (intent, never a promise)
//   carousel        ia.carousel@1; each slide is a group with a controlled
//                   structure: background shape · optional picture · optional caption
//   form            a non-PII FormDecl + one `field` element per question + a
//                   submit button whose tap runs form.submit
//   survey          the survey instrument (a data graph) presented by
//                   ia.survey-question@1 (+ ia.progress@1) in its own scenes
//
// Frames are explicit per format variant (resolved sizes, never scaled).

import {
  SURVEY_END,
  surveySuccessors,
  type Action,
  type AssetRef,
  type CreativeDocument,
  type CreativeOperation,
  type Element,
  type FormDecl,
  type FormField,
  type Frame,
  type PlaybackIntent,
  type Scene,
  type SurveyInstrument,
  type SurveyQuestion,
  type TextContent,
} from "@interactads/creative-model";
import { addElement, assetInUse, captionHeight, patchPresentation, updateShared } from "./commands";
import { findElement, layoutSize, nextId, overrideOf, presentIn, simpleContent } from "./model";

type Ops = CreativeOperation[];
type Size = { width: number; height: number };
type El<T extends Element["type"]> = Extract<Element, { type: T }>;

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const T = (text: string): TextContent => ({ runs: [{ text }] });
const ids = (doc: CreativeDocument) => doc.elements.map((e) => e.id);
/** `base` with `patch` applied; a patch value of undefined removes that key. */
const mergedDefined = (base: object, patch: Readonly<Record<string, unknown>>): Record<string, unknown> =>
  Object.fromEntries(
    [...Object.entries(clone(base)), ...Object.entries(patch)].filter(
      ([k, v], i, all) => v !== undefined && all.findLastIndex(([x]) => x === k) === i
    )
  );

/** Default text/accent colors written EXPLICITLY into new components (no hidden runtime defaults). */
export const COMPONENT_TEXT = "#111827";
export const COMPONENT_ACCENT = "#E4002B";

function centered(artboard: Size, w: number, h: number): Frame {
  const width = Math.max(8, Math.min(Math.round(w), artboard.width));
  const height = Math.max(8, Math.min(Math.round(h), artboard.height));
  return {
    x: Math.round((artboard.width - width) / 2),
    y: Math.round((artboard.height - height) / 2),
    width,
    height,
  };
}

// ─── Video & audio ──────────────────────────────────────────────────────────

/** New video: muted autoplay intent, basic runtime controls, holds the last frame. */
export const VIDEO_PLAYBACK: PlaybackIntent = {
  autoplay: "muted",
  startMuted: true,
  loop: false,
  controls: "minimal",
  preload: "metadata",
  endBehavior: "hold",
};
/** New audio: starts only when the person activates it (sound needs a gesture anyway). */
export const AUDIO_PLAYBACK: PlaybackIntent = {
  autoplay: "none",
  startMuted: false,
  loop: false,
  controls: "minimal",
  preload: "metadata",
};

export function newVideo(
  id: string,
  artboard: Size,
  media: { readonly key: string; readonly asset: AssetRef; readonly name: string },
  onlyIn: readonly string[]
): Element {
  const w = media.asset.width ?? 16;
  const h = media.asset.height ?? 9;
  const scale = Math.min(artboard.width / w, artboard.height / h);
  return {
    id,
    type: "video",
    name: (media.name.trim() || "Video").slice(0, 120),
    asset: media.key,
    fit: "cover",
    frame: centered(artboard, w * scale, h * scale),
    playback: clone(VIDEO_PLAYBACK),
    onlyIn: [...onlyIn],
  };
}

export function newAudio(
  id: string,
  artboard: Size,
  media: { readonly key: string; readonly name: string },
  onlyIn: readonly string[]
): Element {
  const side = Math.max(28, Math.min(44, Math.round(Math.min(artboard.width, artboard.height) / 7)));
  return {
    id,
    type: "audio",
    name: (media.name.trim() || "Audio").slice(0, 120),
    asset: media.key,
    frame: { x: artboard.width - side - 8, y: 8, width: side, height: side },
    playback: clone(AUDIO_PLAYBACK),
    onlyIn: [...onlyIn],
  };
}

/** Merges playback intent fields (shared by every variant); `undefined` values are removed. */
export function setPlayback(
  doc: CreativeDocument,
  id: string,
  patch: Partial<Record<keyof PlaybackIntent, unknown>>
): Ops {
  const el = findElement(doc, id);
  if (!el || (el.type !== "video" && el.type !== "audio")) return [];
  return updateShared(id, { playback: mergedDefined(el.playback, patch) });
}

/** Points a video/audio element to another pinned asset; unpins the previous one when unused. */
export function replaceMedia(doc: CreativeDocument, id: string, key: string): Ops {
  const el = findElement(doc, id);
  if (!el || (el.type !== "video" && el.type !== "audio") || el.asset === key) return [];
  const ops = updateShared(id, { asset: key });
  if (!assetInUse(doc, el.asset, [id])) ops.push({ op: "unpinAsset", key: el.asset });
  return ops;
}

/** Sets (or removes, with null) the poster image of a video. */
export function setPoster(doc: CreativeDocument, id: string, key: string | null): Ops {
  const el = findElement(doc, id);
  if (el?.type !== "video" || el.poster === (key ?? undefined)) return [];
  const ops = key === null ? updateShared(id, {}, ["poster"]) : updateShared(id, { poster: key });
  if (el.poster && !assetInUse(doc, el.poster, [id])) ops.push({ op: "unpinAsset", key: el.poster });
  return ops;
}

// ─── Carousel ───────────────────────────────────────────────────────────────

export const CAROUSEL_REF = "ia.carousel@1";
export const CAROUSEL_MIN_SLIDES = 2;
export const CAROUSEL_MAX_SLIDES = 12;
const SLIDE_COLORS = ["#7C3AED", "#2563EB", "#10B981", "#F59E0B", "#E4002B", "#111827"];

export const isCarousel = (el: Element | undefined): el is El<"component"> =>
  el?.type === "component" && el.component === CAROUSEL_REF;

export const carouselSlides = (doc: CreativeDocument, id: string): string[] => {
  const el = findElement(doc, id);
  return isCarousel(el) ? [...(el.slots?.slides ?? [])] : [];
};

export interface SlideParts {
  readonly slide: El<"group">;
  readonly background?: El<"shape">;
  readonly image?: El<"image">;
  readonly caption?: El<"text">;
  /** The slide holds layers outside the controlled structure (kept untouched). */
  readonly advanced: boolean;
}

export function slideParts(doc: CreativeDocument, slideId: string): SlideParts | null {
  const slide = findElement(doc, slideId);
  if (slide?.type !== "group") return null;
  let background: El<"shape"> | undefined;
  let image: El<"image"> | undefined;
  let caption: El<"text"> | undefined;
  let advanced = false;
  for (const id of slide.children) {
    const el = findElement(doc, id);
    if (el?.type === "shape" && !background) background = el;
    else if (el?.type === "image" && !image) image = el;
    else if (el?.type === "text" && !caption) caption = el;
    else advanced = true;
  }
  return {
    slide,
    ...(background ? { background } : {}),
    ...(image ? { image } : {}),
    ...(caption ? { caption } : {}),
    advanced,
  };
}

/** Size of a (top-level) element in every variant it exists in, in document order. */
function sizesOf(doc: CreativeDocument, id: string): Array<{ layoutId: string; size: Size }> {
  const el = findElement(doc, id);
  if (!el) return [];
  return doc.layouts
    .filter((l) => presentIn(el, l.id))
    .map((l) => {
      const f = overrideOf(doc, l.id, id)?.frame ?? el.frame;
      return { layoutId: l.id, size: { width: f.width, height: f.height } };
    });
}

/**
 * Adds a layer nested in a carousel (slide or slide part) with the right frame
 * in EVERY variant the carousel exists in: base frame for the first one,
 * explicit overrides for variants of another size.
 */
function addNested(
  doc: CreativeDocument,
  carouselId: string,
  element: Element,
  frameFor: (size: Size) => Frame,
  parent: { in: "slot"; id: string; slot: "slides" } | { in: "group"; id: string },
  index?: number
): Ops {
  const sizes = sizesOf(doc, carouselId);
  const first = sizes[0];
  if (!first) return [];
  const base = frameFor(first.size);
  const ops: Ops = [
    {
      op: "addElement",
      element: { ...element, frame: base } as Element,
      parent,
      ...(index !== undefined ? { index } : {}),
    },
  ];
  for (const { layoutId, size } of sizes.slice(1)) {
    const f = frameFor(size);
    if (JSON.stringify(f) !== JSON.stringify(base))
      ops.push({ op: "setLayoutOverride", layout: layoutId, element: element.id, override: { frame: f } });
  }
  return ops;
}

const full = (s: Size): Frame => ({ x: 0, y: 0, width: s.width, height: s.height });
const captionFrame = (s: Size): Frame => {
  const h = captionHeight(s.height);
  return { x: 0, y: s.height - h, width: s.width, height: h };
};

function captionElement(id: string, text: string, size: Size): Element {
  const h = captionHeight(size.height);
  return {
    id,
    type: "text",
    name: "Texto de la diapositiva",
    content: T(text),
    typography: {
      size: Math.max(10, Math.round(h * 0.42)),
      weight: 700,
      color: "#FFFFFF",
      align: "center",
      verticalAlign: "middle",
    },
    style: { fill: { kind: "solid", color: "#111827B3" } },
    frame: captionFrame(size),
  };
}

function slideOps(doc: CreativeDocument, carouselId: string, slideId: string, n: number, index?: number): Ops {
  const color = SLIDE_COLORS[(n - 1) % SLIDE_COLORS.length] ?? "#7C3AED";
  const size = sizesOf(doc, carouselId)[0]?.size ?? { width: 100, height: 100 };
  return [
    ...addNested(
      doc,
      carouselId,
      { id: slideId, type: "group", name: `Diapositiva ${String(n)}`, frame: full(size), children: [] },
      full,
      { in: "slot", id: carouselId, slot: "slides" },
      index
    ),
    ...addNested(
      doc,
      carouselId,
      {
        id: `${slideId}_bg`,
        type: "shape",
        name: "Fondo",
        shape: "rect",
        style: { fill: { kind: "solid", color } },
        frame: full(size),
      },
      full,
      { in: "group", id: slideId }
    ),
    ...addNested(doc, carouselId, captionElement(`${slideId}_txt`, `Diapositiva ${String(n)}`, size), captionFrame, {
      in: "group",
      id: slideId,
    }),
  ];
}

const freeSlideId = (doc: CreativeDocument, carouselId: string, extra: readonly string[] = []) =>
  nextId([...ids(doc), ...extra, ...ids(doc).map((x) => x.replace(/_(bg|img|txt)$/, ""))], `${carouselId}_s`);

/** A carousel with three editable slides (colors + captions; pictures are chosen per slide). */
export function newCarousel(
  doc: CreativeDocument,
  layoutId: string,
  sceneId: string,
  artboard: Size
): { ops: Ops; id: string } {
  const id = nextId(ids(doc), "carousel");
  const frame = centered(artboard, artboard.width, Math.round(artboard.height * 0.72));
  const carousel: Element = {
    id,
    type: "component",
    name: "Carrusel",
    component: CAROUSEL_REF,
    props: {
      autoAdvanceMs: 0,
      loop: true,
      gesture: "swipe",
      indicators: "dots",
      transition: "slide",
      accentColor: COMPONENT_TEXT,
    },
    slots: { slides: [] },
    frame,
    interactive: { label: "Carrusel" },
    onlyIn: [layoutId],
  };
  // Apply the carousel first so the slides can be framed from its size.
  const ops: Ops = addElement(doc, layoutId, carousel, sceneId);
  const withCarousel: CreativeDocument = { ...doc, elements: [...doc.elements, carousel] };
  const taken: string[] = [];
  for (let n = 1; n <= 3; n++) {
    const slideId = freeSlideId(withCarousel, id, taken);
    taken.push(slideId);
    ops.push(...slideOps(withCarousel, id, slideId, n));
  }
  return { ops, id };
}

export function addSlide(doc: CreativeDocument, carouselId: string): { ops: Ops; slideId: string } | null {
  const slides = carouselSlides(doc, carouselId);
  if (slides.length >= CAROUSEL_MAX_SLIDES) return null;
  const slideId = freeSlideId(doc, carouselId);
  return { ops: slideOps(doc, carouselId, slideId, slides.length + 1), slideId };
}

/** A copy of a slide (all its layers, with their frames in every variant), right after it. */
export function duplicateSlide(
  doc: CreativeDocument,
  carouselId: string,
  slideId: string
): { ops: Ops; slideId: string } | null {
  const slides = carouselSlides(doc, carouselId);
  const parts = slideParts(doc, slideId);
  if (slides.length >= CAROUSEL_MAX_SLIDES || !parts) return null;
  const copyId = freeSlideId(doc, carouselId);
  const ops: Ops = [
    {
      op: "addElement",
      element: { ...clone(parts.slide), id: copyId, children: [] },
      parent: { in: "slot", id: carouselId, slot: "slides" },
      index: slides.indexOf(slideId) + 1,
    },
  ];
  const overrides = (id: string, to: string) =>
    doc.layouts.flatMap((l): Ops => {
      const o = overrideOf(doc, l.id, id);
      return o ? [{ op: "setLayoutOverride", layout: l.id, element: to, override: clone(o) }] : [];
    });
  ops.push(...overrides(slideId, copyId));
  parts.slide.children.forEach((childId) => {
    const child = findElement(doc, childId);
    if (!child || child.type === "group" || child.type === "component") return;
    const to = childId.startsWith(`${slideId}_`) ? `${copyId}${childId.slice(slideId.length)}` : nextId(ids(doc), "el");
    ops.push({ op: "addElement", element: { ...clone(child), id: to }, parent: { in: "group", id: copyId } });
    ops.push(...overrides(childId, to));
  });
  return { ops, slideId: copyId };
}

/** Removes a slide and its layers (with the interactions/links/pictures only it used). */
export function removeSlide(
  doc: CreativeDocument,
  carouselId: string,
  slideId: string,
  removeEverywhere: (doc: CreativeDocument, id: string) => Ops
): Ops {
  const slides = carouselSlides(doc, carouselId);
  if (slides.length <= CAROUSEL_MIN_SLIDES || !slides.includes(slideId)) return [];
  return removeEverywhere(doc, slideId);
}

export function moveSlide(doc: CreativeDocument, carouselId: string, slideId: string, toIndex: number): Ops {
  const slides = carouselSlides(doc, carouselId);
  const from = slides.indexOf(slideId);
  const to = Math.max(0, Math.min(slides.length - 1, toIndex));
  if (from < 0 || from === to) return [];
  slides.splice(from, 1);
  slides.splice(to, 0, slideId);
  return updateShared(carouselId, { slots: { slides } });
}

/** Sets the picture of a slide (added full-bleed above its background, or replaced). */
export function setSlideImage(
  doc: CreativeDocument,
  carouselId: string,
  slideId: string,
  key: string,
  alt: string
): Ops {
  const parts = slideParts(doc, slideId);
  if (!parts) return [];
  if (parts.image) {
    if (parts.image.asset === key) return updateShared(parts.image.id, { alt: alt.slice(0, 300) });
    const ops = updateShared(parts.image.id, { asset: key, alt: alt.slice(0, 300) });
    if (!assetInUse(doc, parts.image.asset, [parts.image.id])) ops.push({ op: "unpinAsset", key: parts.image.asset });
    return ops;
  }
  const id = ids(doc).includes(`${slideId}_img`) ? nextId(ids(doc), `${slideId}_img`) : `${slideId}_img`;
  const index = parts.background ? parts.slide.children.indexOf(parts.background.id) + 1 : 0;
  const size = sizesOf(doc, carouselId)[0]?.size ?? { width: 100, height: 100 };
  return addNested(
    doc,
    carouselId,
    { id, type: "image", name: "Imagen", asset: key, fit: "cover", alt: alt.slice(0, 300), frame: full(size) },
    full,
    { in: "group", id: slideId },
    index
  );
}

export function removeSlideImage(doc: CreativeDocument, slideId: string): Ops {
  const image = slideParts(doc, slideId)?.image;
  if (!image) return [];
  const ops: Ops = [{ op: "removeElement", id: image.id }];
  if (!assetInUse(doc, image.asset, [image.id])) ops.push({ op: "unpinAsset", key: image.asset });
  return ops;
}

/** Caption text of a slide: added, changed or (empty) removed. */
export function setSlideCaption(doc: CreativeDocument, carouselId: string, slideId: string, text: string): Ops {
  const parts = slideParts(doc, slideId);
  if (!parts) return [];
  const trimmed = text.trim();
  if (parts.caption)
    return trimmed === ""
      ? [{ op: "removeElement", id: parts.caption.id }]
      : updateShared(parts.caption.id, { content: simpleContent(text, false) });
  if (trimmed === "") return [];
  const size = sizesOf(doc, carouselId)[0]?.size ?? { width: 100, height: 100 };
  const id = ids(doc).includes(`${slideId}_txt`) ? nextId(ids(doc), `${slideId}_txt`) : `${slideId}_txt`;
  return addNested(doc, carouselId, captionElement(id, text, size), captionFrame, { in: "group", id: slideId });
}

export function setSlideColor(doc: CreativeDocument, layoutId: string, slideId: string, color: string): Ops {
  const bg = slideParts(doc, slideId)?.background;
  return bg ? patchPresentation(doc, layoutId, bg.id, { style: { fill: { kind: "solid", color } } }) : [];
}

export function setCarouselProps(doc: CreativeDocument, id: string, patch: Record<string, unknown>): Ops {
  const el = findElement(doc, id);
  if (!isCarousel(el)) return [];
  return updateShared(id, { props: mergedDefined(el.props, patch) });
}

// ─── Forms (non-PII) ────────────────────────────────────────────────────────

/** Answer types offered by the Studio: bounded, closed answers only (no free text, no personal data). */
export const FIELD_KINDS = ["single", "multiple", "dropdown", "rating", "yesno"] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

export function fieldKind(field: FormField): FieldKind | "text" {
  switch (field.control) {
    case "radio":
      return field.options.length === 2 && field.options[0]?.id === "yes" && field.options[1]?.id === "no"
        ? "yesno"
        : "single";
    case "checkbox":
      return "multiple";
    case "select":
      return "dropdown";
    case "rating":
      return "rating";
    default:
      return "text";
  }
}

const defaultOptions = () => [
  { id: "o1", label: T("Opción A") },
  { id: "o2", label: T("Opción B") },
];

/** The same question as another answer type (label and "required" kept; options kept when they fit). */
export function convertField(field: FormField, kind: FieldKind): FormField {
  const base = { id: field.id, label: clone(field.label), required: field.required };
  const options =
    "options" in field && !(field.options[0]?.id === "yes" && field.options.length === 2)
      ? clone(field.options)
      : defaultOptions();
  switch (kind) {
    case "single":
      return { ...base, control: "radio", options, dataClass: "choice" };
    case "multiple":
      return { ...base, control: "checkbox", options, dataClass: "choice" };
    case "dropdown":
      return { ...base, control: "select", options, dataClass: "choice" };
    case "rating":
      return { ...base, control: "rating", scale: { min: 1, max: 5 }, dataClass: "choice" };
    case "yesno":
      return {
        ...base,
        control: "radio",
        options: [
          { id: "yes", label: T("Sí") },
          { id: "no", label: T("No") },
        ],
        dataClass: "choice",
      };
  }
}

export function formOfElement(doc: CreativeDocument, elementId: string): { form: FormDecl; field: FormField } | null {
  const el = findElement(doc, elementId);
  if (el?.type !== "field") return null;
  const form = doc.forms?.find((f) => f.id === el.form);
  const field = form?.fields.find((f) => f.id === el.field);
  return form && field ? { form, field } : null;
}

/** Field elements presenting a form, in document order. */
export const fieldElements = (doc: CreativeDocument, formId: string) =>
  doc.elements.filter((e): e is El<"field"> => e.type === "field" && e.form === formId);

export function setFormField(doc: CreativeDocument, formId: string, field: FormField): Ops {
  const form = doc.forms?.find((f) => f.id === formId);
  if (!form) return [];
  return [
    { op: "setForm", form: { ...clone(form), fields: form.fields.map((f) => (f.id === field.id ? field : clone(f))) } },
  ];
}

function fieldHeight(field: FormField, fontSize: number): number {
  const line = Math.round(fontSize * 1.9);
  if (field.control === "rating" || field.control === "select") return line * 2 + 8;
  if ("options" in field) return line * (field.options.length + 1) + 8;
  return line * 2;
}

function fieldElement(id: string, form: string, field: FormField, frame: Frame, fontSize: number): Element {
  return {
    id,
    type: "field",
    name: "Pregunta del formulario",
    form,
    field: field.id,
    frame: { ...frame, height: Math.max(frame.height, fieldHeight(field, fontSize)) },
    appearance: { textColor: COMPONENT_TEXT, accentColor: COMPONENT_ACCENT, fontSize },
  };
}

const submitRuleId = (formId: string) => `submit_${formId}`;
const afterSubmitRuleId = (formId: string) => `sent_${formId}`;

/**
 * A form: one single-choice question, its field element and an "Enviar"
 * button whose tap submits it (form.submit validates required answers and
 * emits only option ids / scale values).
 */
export function newForm(
  doc: CreativeDocument,
  layoutId: string,
  sceneId: string,
  artboard: Size
): { ops: Ops; elementId: string; formId: string } {
  const formId = nextId(
    (doc.forms ?? []).map((f) => f.id),
    "form"
  );
  const field: FormField = {
    id: "f1",
    control: "radio",
    label: T("¿Qué prefieres?"),
    required: true,
    options: defaultOptions(),
    dataClass: "choice",
  };
  const fontSize = Math.max(11, Math.min(16, Math.round(Math.min(artboard.width, artboard.height) / 18)));
  const elementId = nextId(ids(doc), "field");
  const buttonId = nextId([...ids(doc), elementId], "button");
  const width = Math.min(artboard.width - 24, 320);
  const x = Math.round((artboard.width - width) / 2);
  const el = fieldElement(elementId, formId, field, { x, y: 12, width, height: 0 }, fontSize);
  const btnH = Math.max(28, Math.round(fontSize * 2.6));
  const button: Element = {
    id: buttonId,
    type: "button",
    name: "Enviar",
    role: "button",
    label: T("Enviar"),
    typography: { size: fontSize, weight: 700, color: "#FFFFFF", align: "center", verticalAlign: "middle" },
    style: { fill: { kind: "solid", color: COMPONENT_ACCENT }, radius: 6 },
    interactive: { cursor: "pointer" },
    frame: {
      x: Math.round((artboard.width - Math.min(160, width)) / 2),
      y: Math.min(artboard.height - btnH - 8, el.frame.y + el.frame.height + 8),
      width: Math.min(160, width),
      height: btnH,
    },
    onlyIn: [layoutId],
  };
  const ops: Ops = [
    { op: "setForm", form: { id: formId, fields: [field], submit: { mode: "events" } } },
    ...addElement(doc, layoutId, { ...el, onlyIn: [layoutId] }, sceneId),
    ...addElement(doc, layoutId, button, sceneId),
    {
      op: "addRule",
      rule: {
        id: submitRuleId(formId),
        on: { type: "pointer.tap", target: buttonId },
        do: [{ type: "form.submit", form: formId }],
      },
    },
  ];
  return { ops, elementId, formId };
}

/** Buttons whose tap submits the form (their rule runs form.submit). */
export function submitButtons(doc: CreativeDocument, formId: string): string[] {
  return (doc.rules ?? []).flatMap((r) =>
    r.on.type === "pointer.tap" && r.do.some((a) => a.type === "form.submit" && a.form === formId) ? [r.on.target] : []
  );
}

/**
 * Another question in the same form, placed right below the given field
 * element (same variant). The form's submit button(s) in that variant move
 * below the new question (explicit frames, kept inside the artboard).
 */
export function addFormQuestion(
  doc: CreativeDocument,
  layoutId: string,
  sceneId: string,
  formId: string,
  below: string,
  artboard: Size
): { ops: Ops; elementId: string } | null {
  const form = doc.forms?.find((f) => f.id === formId);
  const ref = findElement(doc, below);
  if (!form || ref?.type !== "field") return null;
  const fieldId = nextId(
    form.fields.map((f) => f.id),
    "f"
  );
  const field: FormField = {
    id: fieldId,
    control: "radio",
    label: T("Nueva pregunta"),
    required: true,
    options: defaultOptions(),
    dataClass: "choice",
  };
  const frame = overrideOf(doc, layoutId, below)?.frame ?? ref.frame;
  const elementId = nextId(ids(doc), "field");
  const el = fieldElement(
    elementId,
    formId,
    field,
    { x: frame.x, y: frame.y + frame.height + 8, width: frame.width, height: 0 },
    ref.appearance?.fontSize ?? 13
  );
  const ops: Ops = [
    { op: "setForm", form: { ...clone(form), fields: [...clone(form.fields), field] } },
    ...addElement(
      doc,
      layoutId,
      { ...el, ...(ref.appearance ? { appearance: clone(ref.appearance) } : {}), onlyIn: [layoutId] },
      sceneId
    ),
  ];
  const bottom = el.frame.y + el.frame.height + 8;
  for (const id of submitButtons(doc, formId)) {
    const button = findElement(doc, id);
    if (!button || !presentIn(button, layoutId)) continue;
    const f = overrideOf(doc, layoutId, id)?.frame ?? button.frame;
    if (f.y >= bottom) continue;
    ops.push(
      ...patchPresentation(doc, layoutId, id, {
        frame: { ...f, y: Math.max(0, Math.min(bottom, artboard.height - f.height)) },
      })
    );
  }
  return { ops, elementId };
}

/** Scene the creative goes to once the form is sent (the Studio's own rule), if any. */
export function afterSubmitScene(doc: CreativeDocument, formId: string): string | undefined {
  const rule = doc.rules?.find((r) => r.id === afterSubmitRuleId(formId));
  const go = rule?.do.find((a) => a.type === "scene.goTo");
  return go?.type === "scene.goTo" ? go.scene : undefined;
}

export function setAfterSubmit(doc: CreativeDocument, formId: string, sceneId: string | null): Ops {
  const id = afterSubmitRuleId(formId);
  const exists = doc.rules?.some((r) => r.id === id) ?? false;
  if (sceneId === null) return exists ? [{ op: "removeRule", id }] : [];
  const rule = {
    id,
    on: { type: "form.submitted" as const, form: formId },
    do: [{ type: "scene.goTo" as const, scene: sceneId }],
  };
  return [exists ? { op: "replaceRule", rule } : { op: "addRule", rule }];
}

/** Whether something submits the form (a rule with form.submit). */
export function isSubmitted(doc: CreativeDocument, formId: string): boolean {
  const submits = (actions: readonly Action[]): boolean =>
    actions.some((a) => (a.type === "form.submit" && a.form === formId) || (a.type === "after" && submits(a.do)));
  return (doc.rules ?? []).some((r) => submits(r.do));
}

// ─── Survey ─────────────────────────────────────────────────────────────────

export const SURVEY_QUESTION_REF = "ia.survey-question@1";
export const SURVEY_PROGRESS_REF = "ia.progress@1";
export const SURVEY_ID = "survey";
export const QUESTION_KINDS = ["single", "multi", "scale"] as const;
export type QuestionKind = (typeof QUESTION_KINDS)[number];

const questionIds = (s: SurveyInstrument) => s.questions.map((q) => q.id);

export function newQuestion(id: string, kind: QuestionKind, next: string): SurveyQuestion {
  const base = { id, category: "research" as const, prompt: T("Escribe la pregunta"), required: true };
  if (kind === "scale") return { ...base, kind, scale: { min: 1, max: 5 }, next };
  if (kind === "multi")
    return {
      ...base,
      kind,
      options: [
        { id: "a", label: T("Opción A") },
        { id: "b", label: T("Opción B") },
      ],
      maxSelections: 2,
      next,
    };
  return {
    ...base,
    kind,
    options: [
      { id: "a", label: T("Opción A"), next },
      { id: "b", label: T("Opción B"), next },
    ],
  };
}

/** Where a question goes when the participant answers without a specific branch. */
export function defaultNext(q: SurveyQuestion): string {
  if (q.kind === "single") return q.next ?? q.options[0]?.next ?? SURVEY_END;
  return q.next;
}

/**
 * Longest respondent path (questions shown) from the start; `cyclic` when a
 * loop exists (the validator reports it; the builder only shows the number).
 */
export function longestPath(s: SurveyInstrument): { longest: number; cyclic: boolean } {
  const memo = new Map<string, number>();
  const grey = new Set<string>();
  let cyclic = false;
  const visit = (id: string): number => {
    const q = s.questions.find((x) => x.id === id);
    if (!q) return 0;
    const known = memo.get(id);
    if (known !== undefined) return known;
    if (grey.has(id)) {
      cyclic = true;
      return 0;
    }
    grey.add(id);
    let best = 0;
    for (const next of surveySuccessors(q))
      if (next !== undefined && next !== SURVEY_END) best = Math.max(best, visit(next));
    grey.delete(id);
    memo.set(id, best + 1);
    return best + 1;
  };
  return { longest: visit(s.startQuestionId), cyclic };
}

/** Every exit of a question that currently leads to `target` (per option for single choice). */
function redirect(q: SurveyQuestion, target: string, to: string): SurveyQuestion {
  if (q.kind === "single")
    return {
      ...q,
      ...(q.next === target ? { next: to } : {}),
      options: q.options.map((o) => (o.next === target ? { ...o, next: to } : o)),
    };
  return q.next === target ? { ...q, next: to } : q;
}

/**
 * Appends a question. Linear by default: the exits of the previously LAST
 * question that ended the survey now lead to the new one (branches the
 * author set elsewhere are kept).
 */
export function addQuestion(s: SurveyInstrument, kind: QuestionKind): { survey: SurveyInstrument; id: string } {
  const id = nextId(questionIds(s), "q");
  const last = s.questions.at(-1);
  const questions = s.questions.map((q) => (q === last ? redirect(q, SURVEY_END, id) : q));
  return { survey: { ...s, questions: [...questions, newQuestion(id, kind, SURVEY_END)] }, id };
}

/**
 * Removes a question. Whatever led to it now leads where it led by default
 * (explicit, announced by the caller); the start moves the same way.
 * Returns null when it is the only question or the start question ends right away.
 */
export function removeQuestion(s: SurveyInstrument, id: string): { survey: SurveyInstrument; to: string } | null {
  const q = s.questions.find((x) => x.id === id);
  if (!q || s.questions.length <= 1) return null;
  const to = defaultNext(q) === id ? SURVEY_END : defaultNext(q);
  if (s.startQuestionId === id && to === SURVEY_END) return null;
  return {
    survey: {
      ...s,
      startQuestionId: s.startQuestionId === id ? to : s.startQuestionId,
      questions: s.questions.filter((x) => x.id !== id).map((x) => redirect(x, id, to)),
    },
    to,
  };
}

export function moveQuestion(s: SurveyInstrument, id: string, delta: -1 | 1): SurveyInstrument {
  const list = [...s.questions];
  const i = list.findIndex((q) => q.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= list.length) return s;
  const [q] = list.splice(i, 1);
  if (q) list.splice(j, 0, q);
  return { ...s, questions: list };
}

export function replaceQuestion(s: SurveyInstrument, q: SurveyQuestion): SurveyInstrument {
  return { ...s, questions: s.questions.map((x) => (x.id === q.id ? q : x)) };
}

/** The same question as another kind (prompt, category and required kept; branching collapses to one exit). */
export function convertQuestion(q: SurveyQuestion, kind: QuestionKind): SurveyQuestion {
  if (q.kind === kind) return q;
  const next = defaultNext(q);
  const options =
    q.kind === "scale"
      ? [
          { id: "a", label: T("Opción A") },
          { id: "b", label: T("Opción B") },
        ]
      : q.options.map((o) => ({ id: o.id, label: o.label, ...(o.image ? { image: o.image } : {}) }));
  const base = { id: q.id, category: q.category, prompt: q.prompt, required: q.required };
  if (kind === "scale") return { ...base, kind, scale: { min: 1, max: 5 }, next };
  if (kind === "multi") return { ...base, kind, options, maxSelections: Math.min(2, options.length), next };
  return { ...base, kind, options: options.map((o) => ({ ...o, next })) };
}

export const surveyOps = (survey: SurveyInstrument | undefined): Ops => [
  survey ? { op: "setSurvey", survey } : { op: "setSurvey" },
];

/** Builds the survey scenes and instrument: a question scene (+ progress) and a thank-you scene. */
export function newSurvey(
  doc: CreativeDocument,
  objective: string
): { ops: Ops; sceneId: string; questionElementId: string } | null {
  if (doc.survey) return null;
  const sceneIds = doc.scenes.map((s) => s.id);
  const sceneId = nextId(sceneIds, "encuesta");
  const thanksId = nextId([...sceneIds, sceneId], "gracias");
  const taken = ids(doc);
  const questionId = nextId(taken, "question");
  const progressId = nextId([...taken, questionId], "progress");
  const thanksText = nextId([...taken, questionId, progressId], "text");
  const background = doc.scenes.find((s) => s.id === doc.initialSceneId)?.background;
  const scene = (id: string, name: string, role: NonNullable<Scene["role"]>, children: string[]): Scene => ({
    id,
    name,
    role,
    duration: null,
    children,
    ...(background ? { background: clone(background) } : {}),
  });
  const master = doc.layouts[0];
  if (!master) return null;
  // Present in every format: base frames from the master size, explicit overrides elsewhere.
  const place = (element: Element, frameFor: (s: Size) => Frame, parent: string): Ops => {
    const base = frameFor(layoutSize(master));
    const ops: Ops = [
      { op: "addElement", element: { ...element, frame: base } as Element, parent: { in: "scene", id: parent } },
    ];
    for (const l of doc.layouts.slice(1)) {
      const f = frameFor(layoutSize(l));
      if (JSON.stringify(f) !== JSON.stringify(base))
        ops.push({ op: "setLayoutOverride", layout: l.id, element: element.id, override: { frame: f } });
    }
    return ops;
  };
  const pad = (s: Size) => Math.max(8, Math.round(Math.min(s.width, s.height) * 0.05));
  const survey: SurveyInstrument = {
    id: SURVEY_ID,
    researchObjective: objective.trim().slice(0, 2_000),
    sampleType: "non_probability",
    consent: { required: false },
    startQuestionId: "q1",
    questions: [{ ...newQuestion("q1", "single", SURVEY_END), prompt: T("¿Cuál prefieres?") }],
    presentation: { sceneId, completionSceneId: thanksId },
  };
  const ops: Ops = [
    { op: "addScene", scene: scene(sceneId, "Encuesta", "survey", []) },
    { op: "addScene", scene: scene(thanksId, "Gracias", "result", []) },
    ...place(
      {
        id: progressId,
        type: "component",
        name: "Progreso de la encuesta",
        component: SURVEY_PROGRESS_REF,
        props: { source: "survey", style: "bar", survey: SURVEY_ID, color: COMPONENT_ACCENT },
        frame: { x: 0, y: 0, width: 1, height: 1 },
      },
      (s) => ({ x: pad(s), y: pad(s), width: s.width - 2 * pad(s), height: Math.max(4, Math.round(pad(s) / 2)) }),
      sceneId
    ),
    ...place(
      {
        id: questionId,
        type: "component",
        name: "Pregunta de la encuesta",
        component: SURVEY_QUESTION_REF,
        props: {
          survey: SURVEY_ID,
          optionLayout: "stack",
          optionStyle: "pill",
          autoAdvance: true,
          textColor: COMPONENT_TEXT,
          accentColor: COMPONENT_ACCENT,
        },
        frame: { x: 0, y: 0, width: 1, height: 1 },
      },
      (s) => ({ x: pad(s), y: pad(s) * 2 + 6, width: s.width - 2 * pad(s), height: s.height - pad(s) * 3 - 6 }),
      sceneId
    ),
    ...place(
      {
        id: thanksText,
        type: "text",
        name: "Agradecimiento",
        content: T("¡Gracias por responder!"),
        typography: { size: 20, weight: 700, color: COMPONENT_TEXT, align: "center", verticalAlign: "middle" },
        frame: { x: 0, y: 0, width: 1, height: 1 },
      },
      (s) => centered(s, s.width - 2 * pad(s), Math.round(s.height * 0.4)),
      thanksId
    ),
    { op: "setSurvey", survey },
  ];
  return { ops, sceneId, questionElementId: questionId };
}

/** Elements that present the survey (question + progress bound to it). */
export const surveyElements = (doc: CreativeDocument) =>
  doc.elements.filter(
    (e) =>
      e.type === "component" &&
      (e.component === SURVEY_QUESTION_REF ||
        (e.component === SURVEY_PROGRESS_REF && (e.props as { source?: string }).source === "survey"))
  );
